import { cn } from "@/lib/utils"

interface FoldingCubeProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: "sm" | "md" | "lg"
  text?: string
}

/**
 * SpinKit Folding Cube Loader
 *
 * A modern loading animation based on SpinKit's folding cube design
 *
 * @example
 * ```tsx
 * <FoldingCube size="md" text="Loading..." />
 * ```
 */
function FoldingCube({
  className,
  size = "md",
  text,
  ...props
}: FoldingCubeProps) {
  const sizeMap = {
    sm: { container: "w-8 h-8", cube: "w-3 h-3", text: "text-xs" },
    md: { container: "w-10 h-10", cube: "w-4 h-4", text: "text-sm" },
    lg: { container: "w-14 h-14", cube: "w-5 h-5", text: "text-base" },
  }

  const sizes = sizeMap[size]

  return (
    <div
      role="status"
      aria-label="Loading"
      className={cn("flex flex-col items-center gap-3", className)}
      {...props}
    >
      <div className={cn("relative", sizes.container)}>
        {/* Cube 1 */}
        <div
          className={cn(
            "absolute top-0 left-0 bg-primary",
            sizes.cube,
            "animate-folding-cube-1"
          )}
        />
        {/* Cube 2 */}
        <div
          className={cn(
            "absolute top-0 right-0 bg-primary",
            sizes.cube,
            "animate-folding-cube-2"
          )}
        />
        {/* Cube 3 */}
        <div
          className={cn(
            "absolute bottom-0 right-0 bg-primary",
            sizes.cube,
            "animate-folding-cube-3"
          )}
        />
        {/* Cube 4 */}
        <div
          className={cn(
            "absolute bottom-0 left-0 bg-primary",
            sizes.cube,
            "animate-folding-cube-4"
          )}
        />
      </div>
      {text && (
        <span className={cn("text-muted-foreground", sizes.text)}>
          {text}
        </span>
      )}
    </div>
  )
}

export { FoldingCube }
export type { FoldingCubeProps }
